import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../Store/store';
import { getSavedInviteParams, clearSavedInviteParams, validateAndSaveUrlParams } from '../utils/urlParams';
import { useLazyValidateInviteQuery } from '../api/userInvite';
import { useJoinTenantByInviteMutation } from '../api/tenant';
import { openModal, closeModal } from '../Store/modalSlice';

type InviteState = {
  status: 'idle' | 'valid' | 'invalid' | 'expired' | 'joined' | 'already_member' | 'none';
  tenant_id?: string;
  tenant_name?: string;
};

/**
 * Handles invitation validation and user flow after authentication
 */
export const useInviteFlow = () => {
  const dispatch = useDispatch();
  const { orgName, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const [inviteState, setInviteState] = useState<InviteState>({ status: 'idle' });

  const [triggerValidateInvite, { data: validationData, status: validationStatus }] = useLazyValidateInviteQuery();
  const [joinTenantByInvite, joinResult] = useJoinTenantByInviteMutation();

  // Save any URL params on mount so they're available before auth completes
  useEffect(() => {
    try { validateAndSaveUrlParams(); } catch (e) {}
  }, []);

  // Validate invite after authentication
  useEffect(() => {
    if (!isAuthenticated) return;
    const { invite_hash } = getSavedInviteParams();
    if (!invite_hash) return;
    triggerValidateInvite({ invitationHash: invite_hash });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Decide UI after validation
  useEffect(() => {
    if (!validationData && validationStatus !== 'rejected') return;

    // Interpret backend messages
    const message = validationData?.message;
    if (message === 'Invitation not found') {
      setInviteState({ status: 'invalid' });
      dispatch(openModal({ type: 'invite-error' as any, width: 'md', props: { reason: 'not_found' } }));
      return;
    }
    if (message === 'Invitation already accepted') {
      setInviteState({ status: 'invalid' });
      dispatch(openModal({ type: 'invite-error' as any, width: 'md', props: { reason: 'already_accepted' } }));
      return;
    }
    const isValid = !!validationData && message === 'Invitation valid.';
    if (!isValid) {
      setInviteState({ status: 'invalid' });
      dispatch(openModal({ type: 'invite-error' as any, width: 'md', props: { reason: 'invalid_or_expired' } }));
      return;
    }

    const isSameTenant = validationData?.tenant_name && validationData.tenant_name === orgName;
    if (isSameTenant) {
      setInviteState({ status: 'valid', tenant_id: validationData?.tenant_id, tenant_name: validationData?.tenant_name });
      clearSavedInviteParams();
      return;
    }

    // Show confirmation modal to switch tenants
    setInviteState({ status: 'valid', tenant_id: validationData?.tenant_id, tenant_name: validationData?.tenant_name });
    dispatch(openModal({ type: 'invite-join-confirm' as any, width: 'md', props: { tenantName: validationData?.tenant_name } }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationData, validationStatus]);

  // Expose state to consumers if needed
  return { inviteState, joinResult };
};

export default useInviteFlow;

